// src/components/subcontractors/modals/JobFormModal.jsx
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSubcontractors } from '../../../context/SubcontractorContext';

const STATUS_OPTIONS = ['scheduled', 'in_progress', 'completed', 'archived'];

const EMPTY_FORM = {
  linkType: 'standalone',
  estimateId: null,
  workOrderId: null,
  customerName: '',
  vehicleLabel: '',
  projectRef: '',
  scope: '',
  agreedPrice: '',
  scheduledDate: '',
  status: 'scheduled',
};

function loadEstimates() {
  try {
    const raw = JSON.parse(localStorage.getItem('wm-estimates-v2') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}
function loadWorkOrders() {
  try {
    const raw = JSON.parse(localStorage.getItem('wm-work-orders-v1') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}

export default function JobFormModal({ subcontractorId, job, onClose }) {
  const { addJob, updateJob } = useSubcontractors();
  // eslint-disable-next-line react-hooks/use-memo
  const estimates  = useMemo(loadEstimates,  []);
  // eslint-disable-next-line react-hooks/use-memo
  const workOrders = useMemo(loadWorkOrders, []);

  const [form,   setForm]   = useState(() => job ? {
    ...EMPTY_FORM, ...job,
    agreedPrice: job.agreedPrice ?? '',
    scheduledDate: job.scheduledDate ? job.scheduledDate.slice(0, 10) : '',
  } : EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: undefined }));
  }

  function setLinkType(lt) {
    setForm(f => ({ ...f, linkType: lt, estimateId: null, workOrderId: null }));
    setSearch('');
    setErrors(e => ({ ...e, linkType: undefined }));
  }

  function selectEstimate(est) {
    setForm(f => ({
      ...f,
      estimateId:   est.id,
      workOrderId:  null,
      customerName: est.customerName || est.customer || '',
      vehicleLabel: est.vehicleLabel || est.vehicle || '',
      projectRef:   est.estimateNumber || est.id,
    }));
    setSearch('');
  }

  function selectWorkOrder(wo) {
    setForm(f => ({
      ...f,
      workOrderId:  wo.id,
      estimateId:   null,
      customerName: wo.customerName || wo.customer || '',
      vehicleLabel: wo.vehicleLabel || wo.vehicle || '',
      projectRef:   wo.orderNumber || wo.id,
    }));
    setSearch('');
  }

  const linkedResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    if (form.linkType === 'estimate') {
      return estimates.filter(e =>
        (e.customerName || '').toLowerCase().includes(q) ||
        (e.estimateNumber || '').toLowerCase().includes(q)
      ).slice(0, 6);
    }
    return workOrders.filter(wo =>
      (wo.customerName || wo.customer || '').toLowerCase().includes(q) ||
      (wo.orderNumber || '').toLowerCase().includes(q)
    ).slice(0, 6);
  }, [search, form.linkType, estimates, workOrders]);

  function validate() {
    const e = {};
    if (!form.customerName.trim()) e.customerName = 'Required';
    if (!form.scope.trim())        e.scope = 'Required';
    if (!form.agreedPrice || isNaN(Number(form.agreedPrice)) || Number(form.agreedPrice) <= 0)
      e.agreedPrice = 'Enter a valid amount greater than 0';
    return e;
  }

  function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const data = {
      ...form,
      subcontractorId,
      customerName:  form.customerName.trim(),
      vehicleLabel:  form.vehicleLabel.trim(),
      projectRef:    form.projectRef.trim(),
      scope:         form.scope.trim(),
      agreedPrice:   Number(form.agreedPrice),
      scheduledDate: form.scheduledDate ? new Date(form.scheduledDate + 'T12:00:00').toISOString() : null,
    };
    if (job) updateJob(job.id, data);
    else     addJob(data);
    onClose();
  }

  // Stable Escape handler
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const inputCls = 'w-full h-8 px-3 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[var(--wm-text-ui)] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]/30 transition-colors';
  const labelCls = 'block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="bg-white dark:bg-[#111C27] border border-gray-200 dark:border-[#1E3048] rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-[#1E3048]">
          <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">{job ? 'Edit Job' : 'Assign Job'}</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {/* Link type */}
          <div>
            <label className={labelCls}>Link Type</label>
            <div className="flex gap-2">
              {['estimate', 'work_order', 'standalone'].map(lt => (
                <button
                  key={lt}
                  onClick={() => setLinkType(lt)}
                  className="flex-1 py-1.5 text-[var(--wm-text-ui)] rounded-lg border capitalize transition-colors"
                  style={{
                    background:  form.linkType === lt ? '#2E8BF022' : 'transparent',
                    borderColor: form.linkType === lt ? '#2E8BF0'   : 'var(--wm-nav-border)',
                    color:       form.linkType === lt ? '#2E8BF0'   : 'var(--wm-nav-text)',
                  }}
                >
                  {lt === 'work_order' ? 'Work Order' : lt.charAt(0).toUpperCase() + lt.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Linked record search */}
          {form.linkType !== 'standalone' && (
            <div className="relative">
              <label className={labelCls}>
                Search {form.linkType === 'estimate' ? 'Estimate' : 'Work Order'}
              </label>
              <input
                className={inputCls}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by customer or reference…"
              />
              {linkedResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-lg overflow-hidden shadow-xl">
                  {linkedResults.map(r => (
                    <button
                      key={r.id}
                      onClick={() => form.linkType === 'estimate' ? selectEstimate(r) : selectWorkOrder(r)}
                      className="w-full text-left px-4 py-2 text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors"
                    >
                      <span className="font-medium">{r.customerName || r.customer}</span>
                      <span className="text-[#64748B] dark:text-[#7D93AE] ml-2">{r.estimateNumber || r.orderNumber || r.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Customer + Vehicle */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Customer <span className="text-red-400">*</span></label>
              <input className={inputCls} value={form.customerName} onChange={e => set('customerName', e.target.value)} placeholder="Marcus Bell" />
              {errors.customerName && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.customerName}</p>}
            </div>
            <div>
              <label className={labelCls}>Vehicle</label>
              <input className={inputCls} value={form.vehicleLabel} onChange={e => set('vehicleLabel', e.target.value)} placeholder="2023 Tesla Model 3" />
            </div>
          </div>

          {/* Project Ref */}
          <div>
            <label className={labelCls}>Project Ref</label>
            <input className={inputCls} value={form.projectRef} onChange={e => set('projectRef', e.target.value)} placeholder="WM-0041" />
          </div>

          {/* Scope */}
          <div>
            <label className={labelCls}>Scope of Work <span className="text-red-400">*</span></label>
            <textarea className={inputCls + ' !h-auto py-2 resize-y'} rows={3} value={form.scope} onChange={e => set('scope', e.target.value)} placeholder="Describe the work to be done…" />
            {errors.scope && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.scope}</p>}
          </div>

          {/* Agreed Price + Scheduled Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Agreed Price <span className="text-red-400">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-body)]">$</span>
                <input className={inputCls + ' pl-6'} type="number" min="0" value={form.agreedPrice} onChange={e => set('agreedPrice', e.target.value)} placeholder="0" />
              </div>
              {errors.agreedPrice && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.agreedPrice}</p>}
            </div>
            <div>
              <label className={labelCls}>Scheduled Date</label>
              <input className={inputCls} type="date" value={form.scheduledDate} onChange={e => set('scheduledDate', e.target.value)} />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s} className="bg-[#0F1923] text-[#F8FAFE]">
                  {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#1E3048]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">Cancel</button>
          <button onClick={handleSave} className="px-5 py-2 bg-[#2E8BF0] hover:bg-[#1a7de0] text-white text-[var(--wm-text-body)] font-medium rounded-lg transition-colors">
            {job ? 'Save Changes' : 'Assign Job'}
          </button>
        </div>
      </div>
    </div>
  );
}
